import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight, Tag } from 'lucide-react';
import { CTAStrip } from '../components/CTAStrip';
type BlogCategory = 'all' | 'recycling' | 'moving' | 'legal' | 'commercial' | 'news';
const posts = [{
  id: 1,
  category: 'recycling',
  title: '10 Recycling Tips Every London Homeowner Should Know',
  excerpt: "Reduce your household waste and make the most of London's recycling facilities with these practical tips from our waste experts.",
  img: 'https://images.unsplash.com/photo-1532996122724-e3c354a0b15b?w=600&auto=format&fit=crop',
  author: 'James Carter',
  date: '15 Nov 2024',
  readTime: '5 min read'
}, {
  id: 2,
  category: 'moving',
  title: 'How to Prepare for a Stress-Free House Move',
  excerpt: "Moving home doesn't have to be chaotic. Our experienced team shares the ultimate checklist for a smooth, organised move.",
  img: 'https://images.unsplash.com/photo-1486325212027-8081e485255e?w=600&auto=format&fit=crop',
  author: 'Sarah Mitchell',
  date: '8 Nov 2024',
  readTime: '7 min read'
}, {
  id: 3,
  category: 'legal',
  title: "Builder's Waste: Legal Requirements in the UK",
  excerpt: 'Everything contractors and homeowners need to know about legally disposing of construction and demolition waste in England.',
  img: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600&auto=format&fit=crop',
  author: 'David Okafor',
  date: '1 Nov 2024',
  readTime: '6 min read'
}, {
  id: 4,
  category: 'commercial',
  title: 'How Commercial Waste Contracts Work',
  excerpt: 'A plain-English guide to commercial waste contracts — what to look for, what to avoid, and how to get the best value.',
  img: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&auto=format&fit=crop',
  author: 'James Carter',
  date: '25 Oct 2024',
  readTime: '8 min read'
}, {
  id: 5,
  category: 'news',
  title: 'Council Waste Partnership Updates 2024',
  excerpt: 'The latest updates on council waste management partnerships in North London, East London, and surrounding areas.',
  img: 'https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=600&auto=format&fit=crop',
  author: 'Sarah Mitchell',
  date: '18 Oct 2024',
  readTime: '4 min read'
}, {
  id: 6,
  category: 'commercial',
  title: 'The Complete Guide to Office Clearances',
  excerpt: "Planning an office clearance? From IT equipment to furniture, here's everything you need to know for a smooth, compliant clear-out.",
  img: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&auto=format&fit=crop',
  author: 'David Okafor',
  date: '10 Oct 2024',
  readTime: '9 min read'
}];
const categoryTabs: {
  key: BlogCategory;
  label: string;
}[] = [{
  key: 'all',
  label: 'All Posts'
}, {
  key: 'recycling',
  label: 'Recycling'
}, {
  key: 'moving',
  label: 'Moving Tips'
}, {
  key: 'legal',
  label: 'Legal'
}, {
  key: 'commercial',
  label: 'Commercial'
}, {
  key: 'news',
  label: 'News'
}];
const categoryColors: Record<BlogCategory, string> = {
  all: '#1B8F4B',
  recycling: '#1B8F4B',
  moving: '#3B82F6',
  legal: '#8B5CF6',
  commercial: '#F59E0B',
  news: '#EF4444'
};
export function BlogPage() {
  const [activeCategory, setActiveCategory] = useState<BlogCategory>('all');
  const filtered = activeCategory === 'all' ? posts : posts.filter(p => p.category === activeCategory);
  return <div className="pt-16 lg:pt-20" data-id="element-288">
      {/* Hero */}
      <section className="py-16 px-4" style={{
      background: 'linear-gradient(135deg, #1B8F4B 0%, #0F5D2E 100%)'
    }} data-id="element-289">
        <div className="max-w-7xl mx-auto text-center" data-id="element-290">
          <p className="text-green-300 text-sm font-bold uppercase tracking-widest mb-3" data-id="element-291">
            Insights & Advice
          </p>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4" style={{
          fontFamily: 'Montserrat, sans-serif'
        }} data-id="element-292">
            The Universal Global Logistics Blog
          </h1>
          <p className="text-green-100 text-lg max-w-2xl mx-auto" data-id="element-293">
            Expert advice on waste management, moving tips, recycling, and
            industry news.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-16 lg:top-20 z-30 bg-white border-b border-gray-200 shadow-sm" data-id="element-294">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-295">
          <div className="flex gap-2 overflow-x-auto py-3" data-id="element-296">
            {categoryTabs.map(({
            key,
            label
          }) => <button key={key} onClick={() => setActiveCategory(key)} className="px-5 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all duration-200" style={{
            backgroundColor: activeCategory === key ? '#1B8F4B' : 'transparent',
            color: activeCategory === key ? '#fff' : '#6B7280'
          }} data-id="element-297">
                {label}
              </button>)}
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-16 px-4 bg-gray-50" data-id="element-298">
        <div className="max-w-7xl mx-auto" data-id="element-299">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-300">
            {filtered.map(post => <article key={post.id} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 flex flex-col" data-id="element-301">
                <div className="relative h-48 overflow-hidden" data-id="element-302">
                  <img src={post.img} alt={post.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" data-id="element-303" />
                  <span className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-bold text-white capitalize" style={{
                backgroundColor: categoryColors[post.category as BlogCategory]
              }} data-id="element-304">
                    {post.category}
                  </span>
                </div>
                <div className="p-6 flex flex-col flex-1" data-id="element-305">
                  <div className="flex items-center gap-4 text-xs text-gray-400 mb-3" data-id="element-306">
                    <span className="flex items-center gap-1" data-id="element-307">
                      <User className="w-3 h-3" data-id="element-308" /> {post.author}
                    </span>
                    <span className="flex items-center gap-1" data-id="element-309">
                      <Calendar className="w-3 h-3" data-id="element-310" /> {post.date}
                    </span>
                    <span data-id="element-311">{post.readTime}</span>
                  </div>
                  <h2 className="text-lg font-bold text-gray-900 mb-2 leading-snug flex-1" style={{
                fontFamily: 'Montserrat, sans-serif'
              }} data-id="element-312">
                    {post.title}
                  </h2>
                  <p className="text-gray-500 text-sm mb-4 leading-relaxed" data-id="element-313">
                    {post.excerpt}
                  </p>
                  <Link to={`/blog/${post.id}`} className="inline-flex items-center gap-1 text-sm font-semibold hover:underline mt-auto" style={{
                color: '#1B8F4B'
              }} data-id="element-314">
                    Read More <ArrowRight className="w-3 h-3" data-id="element-315" />
                  </Link>
                </div>
              </article>)}
          </div>
        </div>
      </section>

      <CTAStrip data-id="element-316" />
    </div>;
}